import { useCallback, useState } from "react";

import { inputModelToViewModelConverter, viewModelToInputModelConverter } from "@/components/features/_model/converter";

import { serverModelToInputModel } from "./converter/server-model-to-input-model";
import { useHotelSettingsHotelInfoFormModelMethods } from "./methods";
import { excludeKeys, type ExcludeKeys, type HotelSettingsHotelInfoFormModel, type ServerModel } from "../../type";
import { hotelInfoInputZodSchema } from "../../validation-schema";

import type { HotelInfoZodSchemaType } from "../../validation-schema";

type Props = {
	serverModel: ServerModel;
};

type ViewModel = HotelSettingsHotelInfoFormModel["ViewModel"];
type InputModel = HotelSettingsHotelInfoFormModel["InputModel"];
type QueryData = HotelSettingsHotelInfoFormModel["QueryData"];
export type HotelSettingsHotelInfoFormModelOnSetViewModelType = <K extends keyof ViewModel>(
	values: { key: K; value: ViewModel[K] }[],
) => void;

export const useCreateHotelInfoFormModel = ({ serverModel }: Props) => {
	const inputModel = serverModelToInputModel({ serverModel });

	const initialViewModel = inputModelToViewModelConverter<InputModel, ExcludeKeys, QueryData, HotelInfoZodSchemaType>(
		{
			defaultInput: inputModel,
			initInput: undefined,
			currentInput: inputModel,
			excludeKeys,
			zodSchema: hotelInfoInputZodSchema,
		},
	);

	const [viewModel, setViewModel] = useState<ViewModel>(initialViewModel);

	const onSetViewModel: HotelSettingsHotelInfoFormModelOnSetViewModelType = useCallback(
		(values) => {
			const mergedViewModel = {
				...viewModel,
				...values.reduce((acc, cur) => {
					acc[cur.key] = cur.value;
					return acc;
				}, {} as ViewModel),
			} as ViewModel;

			const convertedInputModel = viewModelToInputModelConverter<InputModel, ExcludeKeys, QueryData>(
				mergedViewModel,
			);

			const convertedViewModel = inputModelToViewModelConverter<
				InputModel,
				ExcludeKeys,
				QueryData,
				HotelInfoZodSchemaType
			>({
				defaultInput: inputModel,
				initInput: inputModel,
				currentInput: convertedInputModel,
				excludeKeys,
				zodSchema: hotelInfoInputZodSchema,
			});

			setViewModel(convertedViewModel);
		},
		[viewModel, inputModel, setViewModel],
	);

	const resetViewModel = useCallback(() => {
		setViewModel((prev) => ({
			...prev,
			isDirty: false,
		}));
	}, []);

	return {
		viewModel,
		resetViewModel,
		...useHotelSettingsHotelInfoFormModelMethods({ viewModel, onSetViewModel }),
	};
};
